import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTabActive } from "../../lib/tabActive";
import { Icon } from "./Icon";

/**
 * Stillenmiş onay modalı — native window.confirm yerine.
 * Ortalanmış, ESC + backdrop kapatır, danger/primary tonu.
 */
interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "danger" | "primary";
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Onayla",
  cancelLabel = "Vazgeç",
  tone = "primary",
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // capture + stopPropagation: diyalog en üstteki katman; ESC altındaki drawer'a ulaşmamalı
      // (yoksa "Sil"den vazgeçen kullanıcı drawer'ı da kapatıp kaydedilmemiş formu kaybediyordu).
      if (e.key === "Escape") { e.stopPropagation(); onCancel(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onCancel]);

  const tabActive = useTabActive();
  if (!open) return null;
  // PASİF SEKMEDE DOM'A BASILMAZ. Portal `document.body`ye gider, yani sekme panelinin
  // `display:none` gizlemesi bu katmana ULAŞMAZ — gizli sekmenin paneli aktif sayfanın
  // üstünde durmaya devam ederdi. Burada `null` dönmek DOM'u düşürür, katmanı açan durum
  // (hangi kayıt, forma ne yazıldı) sayfanın React state'inde YAŞAMAYA DEVAM eder.
  if (!tabActive) return null;


  return createPortal(
    <div className="cdlg-backdrop" role="presentation" onClick={onCancel}>
      <div
        className="cdlg"
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <span className={`cdlg-icon ${tone}`} aria-hidden="true">
          <Icon name="alertTriangle" size={20} />
        </span>
        <div className="cdlg-body">
          <h3 className="cdlg-title">{title}</h3>
          <div className="cdlg-msg">{message}</div>
        </div>
        <div className="cdlg-foot">
          <button type="button" className="btn-md secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`btn-md ${tone === "danger" ? "danger" : "primary"}`}
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {busy ? "İşleniyor…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
